import { GetStaticProps } from "next";
import React from "react";
import { getAllWebinars } from "../../lib/api";
import Layout from "../../components/layout";
import Link from "../../components/Link";
import parse from "html-react-parser";
import dayjs from "dayjs";
import customParseFormat from "dayjs/plugin/customParseFormat";
import SearchBlog from "../../components/v2/blog/search-blog";
import CallToActionBlog from "../../components/v2/blog/call-to-action-blog";
import { serverSideTranslations } from "next-i18next/serverSideTranslations";
import nextI18NextConfig from "../../next-i18next.config";
import "../../styles/scss/blog.module.scss";
import CallToActionSmall from "../../components/v2/call-to-action-sm";

dayjs.extend(customParseFormat);

export default function Webinars({ webinars }) {
  const edges = webinars?.edges || [];
  const heroPost = edges?.[0]?.node;
  const belowPost = edges.slice(1, 9);

  const getParsedWebinarDate = (post) => {
    const dateValue = post?.acf_webinars?.dateTime || post?.date;
    const dateString = typeof dateValue === "string" ? dateValue.trim() : "";
    return (
      [
        dayjs(dateString),
        dayjs(dateString, "YYYY-MM-DD HH:mm:ss", true),
        dayjs(dateString, "YYYY-MM-DDTHH:mm:ss", true),
        dayjs(dateString, "YYYY-MM-DDTHH:mm", true),
        dayjs(dateString, "MM/DD/YYYY hh:mm A", true),
        dayjs(dateString.replace(" ", "T")),
      ].find((value) => value.isValid()) || null
    );
  };

  const webinarDate = (post) => {
    const parsedDate = getParsedWebinarDate(post);
    return parsedDate ? parsedDate.format("D MMM YYYY") : "";
  };

  const getTags = (post) => {
    const tags = post?.tags?.edges;
    if (!Array.isArray(tags)) return [];
    return tags.map((tag) => tag?.node?.name).filter(Boolean);
  };

  const getSpeakerHostInfo = (post) => {
    const rawSpeakers = post?.acf_webinars?.speakers;
    const speakers = Array.isArray(rawSpeakers)
      ? rawSpeakers
      : rawSpeakers && typeof rawSpeakers === "object"
        ? [rawSpeakers]
        : [];
    const firstSpeaker = speakers.find((speaker) => speaker?.speakerName || speaker?.hostedBy) || null;
    return {
      speakerName: firstSpeaker?.speakerName || "",
      hostedBy: firstSpeaker?.hostedBy || "",
      hostedByLink: firstSpeaker?.hostedByLink || "",
    };
  };

  return (
    <Layout
      isHomePage={false}
      pageTitle={"Webinars by Middleware | DevOps, AIOps & Observability"}
      ogImage={"/images/middleware-webinar.webp"}
      metaTitle={"Webinars by Middleware | DevOps, AIOps & Observability"}
      metaDesc={
        "Explore upcoming and on-demand Middleware webinars on DevOps, AIOps, and observability. Learn from experts to improve monitoring, automation, and performance."
      }
      uri={"/webinar/"}
      metaRobotsNoindex={"index"}
      metaRobotsNofollow={"follow"}
    >
      <div className={"middle-wrapper site-page blog-v2 "}>
        <div className={"blog-main-page"}>
          <div className={`blog-hero webinars-hero`}>
            <div className={"container"}>
              <div className={"header-section"}>
                <div className={"d-flex"}>
                  <div className={"left-block"}>
                    <div className={"category-title"}>Middleware Webinar</div>
                    <h1>
                      <span className={"text-gradient"}>Observability</span>{" "}
                      AI & Automation
                    </h1>
                    <p>Explore a series of practical webinars designed to help engineering teams improve observability, reduce alert fatigue, and automate issue resolution using modern tools and AI-driven approaches.</p>
                  </div>
                  <div className={"right-block"}>
                    <SearchBlog searchInputPostDetails={edges} parentSlug={"webinar"} />
                  </div>
                </div>
              </div>
            </div>
          </div>

          {heroPost && (
            <div className={"blogs podcast-hero"}>
              <div className={"container"}>
                <div className={"d-flex"}>
                  <div className={"left-block"}>
                    <Link href={`/webinar/${heroPost?.slug || ""}`} itemProp="url">
                      <img
                        src={heroPost?.featuredImage?.node?.sourceUrl}
                        alt={heroPost?.featuredImage?.node?.altText || heroPost?.title}
                        loading={"lazy"}
                        width={100}
                        height={100}
                      />
                    </Link>
                  </div>

                  <div className={"right-block"}>
                    <div className={"category-episode"}>
                      {getTags(heroPost).map((tag, index) => (
                        <div
                          className={`category ${index === 0 ? "gradient-category" : "gray"}`}
                          key={`${heroPost?.slug}-tag-${tag}-${index}`}
                        >
                          {tag}
                        </div>
                      ))}
                    </div>

                    <Link href={`/webinar/${heroPost?.slug || ""}`} itemProp="url">
                      <h2>{heroPost?.title}</h2>
                    </Link>
                    {heroPost?.excerpt ? parse(heroPost.excerpt) : null}
                    <div className={"podcast-guest"}>
                      {(() => {
                        const { speakerName, hostedBy, hostedByLink } = getSpeakerHostInfo(heroPost);
                        return (
                          <p className="hosted-by-webinar">
                            {speakerName ? <>Speaker appearance {speakerName}</> : null}
                            {speakerName && hostedBy ? " • " : ""}
                            {hostedBy ? (
                              <>
                                Hosted by{" "}
                                {hostedByLink ? (
                                  <Link href={hostedByLink} target={"_blank"} skipLocaleHandling={true}>
                                    <span>{hostedBy}</span>
                                  </Link>
                                ) : (
                                  <span>{hostedBy}</span>
                                )}
                              </>
                            ) : null}
                          </p>
                        );
                      })()}
                    </div>
                  </div>
                </div>
              </div>
            </div>
          )}

          <section className={"all-blogs-show site-page all-blogs-v2 webinar-page-middle"}>
            <div className={"container"}>
              <div className={"content-container"}>
                <div className={"content-wrapper"}>
                  <div className={"d-flex"}>
                    {belowPost.map((__post) => {
                      const post = __post?.node;
                      const title = post?.title;
                      return (
                        <div className={"inner-block"} key={post?.uri}>
                          <Link href={`/webinar/${post?.slug || ""}`} itemProp="url">
                            <div className={"image-wrapper"}>
                              <img
                                src={post?.featuredImage?.node?.sourceUrl}
                                alt={post?.featuredImage?.node?.altText || title}
                              />
                            </div>
                          </Link>
                          <div className={"category-episode"}>
                            {getTags(post).map((tag, index) => (
                              <div
                                className={`category ${index === 0 ? "gradient-category" : "gray"}`}
                                key={`${post?.slug}-tag-${tag}-${index}`}
                              >
                                {tag}
                              </div>
                            ))}
                          </div>
                          <div>
                            <div key={post?.uri}>
                              <article className="post-list-item" itemScope itemType="http://schema.org/Article">
                                <header>
                                  <Link href={`/webinar/${post?.slug || ""}`} itemProp="url">
                                    <h4>{title ? parse(title) : ""}</h4>
                                  </Link>
                                  <Link href={`/webinar/${post?.slug || ""}`} className={"read-more"}>
                                    Watch Webinar
                                  </Link>
                                </header>
                              </article>
                            </div>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>
              </div>
            </div>
          </section>

          <CallToActionBlog title={"Never Miss a Webinar: Subscribe for Updates"} />

        </div>

        <CallToActionSmall />
      </div>
    </Layout>
  );
}

export const getStaticProps: GetStaticProps = async (ctx) => {
  const translations = await serverSideTranslations(
    ctx?.locale ?? "en",
    ["common", "header", "footer"],
    nextI18NextConfig
  );
  const webinars = await getAllWebinars();

  return {
    props: {
      ...translations,
      webinars,
      pageTitle: "Webinars by Middleware | DevOps, AIOps & Observability",
      ogImage: "/images/middleware-webinar.webp",
      metaTitle: "Webinars by Middleware | DevOps, AIOps & Observability",
      metaDesc:
        "Explore upcoming and on-demand Middleware webinars on DevOps, AIOps, and observability. Learn from experts to improve monitoring, automation, and performance.",
      description:
        "Explore live and on-demand Middleware webinars on observability, monitoring, and performance best practices.",
      uri: "/webinar/",
      metaRobotsNoindex: "index",
      metaRobotsNofollow: "follow",
    },
  };
};
